/* global globalThis */
import { types, getRoot } from 'mobx-state-tree';
import { fetchAssessmentProgress, saveAssessmentAnswer } from 'api';
import Router from 'next/router';
import { v4 as uuidv4 } from 'uuid';

import { AssessmentProgress } from './models';

// Prefer the native API where available (modern browsers, Node ≥ 19); fall
// back to the uuid package for older runtimes and non-secure contexts.
const createUuid = () =>
  globalThis.crypto && typeof globalThis.crypto.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : uuidv4();

// Client-minted id so concurrent first-saves all target the same row.
const emptyAssessmentProgress = () => ({
  id: createUuid(),
  progressMap: [],
});

export const QuizStore = types
  .model('QuizStore', {
    quizLessonIdx: -1,
    moduleIndex: -1,
    nestedModuleIndex: -1,
    currentLessonId: types.maybeNull(types.string),
    assessmentProgress: types.maybeNull(AssessmentProgress),
    loadingProgress: false,
    currentQuestionIdx: -1,
    savingAnswer: false,
    isQuizActive: false,
    currentQuizToRefetch: types.maybeNull(types.string),
  })
  .views((self) => ({
    get quizLesson() {
      if (self.quizLessonIdx < 0) {
        return null;
      }
      const {
        classDetailsStore: { activeClass },
      } = getRoot(self);

      return activeClass.allLessons[self.quizLessonIdx];
    },

    get currentQuestion() {
      if (self.currentQuestionIdx < 0 || !self.quizLesson) {
        return null;
      }

      return self.quizLesson.assessmentQuestions[self.currentQuestionIdx];
    },

    get currentQuestionCompleted() {
      const { currentQuestion, assessmentProgress } = self;
      if (!currentQuestion || !assessmentProgress) {
        return false;
      }

      return assessmentProgress.progressMap.find((p) => p.questionId === currentQuestion.id);
    },

    // Week number as shown across the platform: orientation modules don't
    // count (see getModuleLabel in components/class/hcc/module/list.js), so
    // the quiz of the Nth curriculum week is "Section N" regardless of how
    // many orientation modules precede it.
    get sectionNumber() {
      if (self.moduleIndex < 0) {
        return null;
      }
      const {
        classDetailsStore: { activeClass },
      } = getRoot(self);

      // moduleIndex can be stale or out-of-range after a class refetch; bail
      // to the fallback label rather than dereferencing a missing module.
      const module = activeClass.modules[self.moduleIndex];
      if (!module) {
        return null;
      }
      const weekModules = activeClass.modules.filter((m) => m.type === 'curriculum');
      const weekIdx = weekModules.findIndex((m) => m.id === module.id);

      // Quiz inside an orientation (or untyped) module: fall back to the raw
      // position rather than a curriculum week number.
      return (weekIdx === -1 ? self.moduleIndex : weekIdx) + 1;
    },

    // Index of the first question in assessmentQuestions whose id is not yet in
    // progressMap. Used for resume so that a non-contiguous progressMap (e.g. a
    // user whose earlier answers failed to persist) still lands the user on a
    // real unanswered question instead of skipping past gaps.
    get firstUnansweredQuestionIdx() {
      const { quizLesson, assessmentProgress } = self;
      const questions = quizLesson && quizLesson.assessmentQuestions;
      if (!questions || !assessmentProgress) return 0;
      const answeredIds = new Set(assessmentProgress.progressMap.map((p) => p.questionId));
      const idx = questions.findIndex((q) => !answeredIds.has(q.id));
      return idx === -1 ? Math.max(0, questions.length - 1) : idx;
    },
  }))
  .actions((self) => ({
    updateDetails(newIdx) {
      const { classDetailsStore } = getRoot(self);
      self.quizLessonIdx = newIdx;

      const lesson = classDetailsStore.activeClass.allLessons[newIdx];

      lesson.randomizeQuizAnswers();

      self.moduleIndex = classDetailsStore.activeClass.modules.findIndex((m) =>
        m.containsLessonId(lesson.id)
      );
      const currentModule =
        self.moduleIndex !== -1 ? classDetailsStore.activeClass.modules[self.moduleIndex] : null;

      self.nestedModuleIndex = currentModule?.modules?.length
        ? currentModule.modules.findIndex((m) => m.containsLessonId(lesson.id))
        : -1;
      self.currentLessonId = lesson.id;
    },

    init(initialLessonId) {
      const {
        classDetailsStore: { activeClass },
      } = getRoot(self);

      // The store outlives individual quizzes; both values are keyed to the
      // previous lesson and must not leak into the one being opened. Nulling
      // assessmentProgress also lets fetchSuccess mint a fresh progress row
      // instead of retaining (and later saving against) the old quiz's.
      self.currentQuestionIdx = -1;
      self.assessmentProgress = null;
      // A save from the previous quiz may still be in flight; clear the flag so
      // the newly opened quiz isn't locked (inputs disabled) waiting on it. Its
      // resolution is ignored by saveAnswerSuccess via the progress-id guard.
      self.savingAnswer = false;

      let idx = activeClass.nextQuizIdx;
      if (initialLessonId) {
        if (activeClass.isLessonViewableMap[initialLessonId]) {
          idx = activeClass.allLessons.findIndex((l) => l.id === initialLessonId);
        } else {
          Router.replace('/user/classes/[slug]/quiz', `/user/classes/${activeClass.urlKey}/quiz`, {
            shallow: true,
          });
          return;
        }
      }

      if (idx < 0) {
        Router.replace('/user/classes/[slug]', `/user/classes/${activeClass.urlKey}`);
      } else {
        self.updateDetails(idx);
        // Fetch progress for the lesson actually resolved — which can differ
        // from the query param (bare /quiz falls back to nextQuizIdx) — so
        // progress always belongs to the quiz being displayed.
        self.fetchProgress(activeClass.allLessons[idx].id);
      }
    },

    async fetchProgress(lessonId) {
      self.loadingProgress = true;
      const {
        classDetailsStore: { activeClass },
      } = getRoot(self);

      try {
        const data = await fetchAssessmentProgress(
          activeClass.id,
          lessonId,
          activeClass.term ? activeClass.term.id : undefined
        );
        self.fetchSuccess(data, lessonId);
      } catch (error) {
        console.error(error);
        self.fetchFailure(lessonId);
      }
    },

    fetchSuccess(data, lessonId) {
      // A slow response for a quiz the user has since navigated away from
      // must not overwrite the progress of the quiz currently displayed.
      if (lessonId !== self.currentLessonId) return;
      self.loadingProgress = false;
      // Preserve any in-flight local id; only mint when we have nothing.
      if (data) {
        self.assessmentProgress = data;
      } else if (!self.assessmentProgress) {
        self.assessmentProgress = emptyAssessmentProgress();
      }
    },

    fetchFailure(lessonId) {
      if (lessonId !== self.currentLessonId) return;
      self.loadingProgress = false;
      // Still let the user start; first saveAnswer reconciles against DDB.
      if (!self.assessmentProgress) {
        self.assessmentProgress = emptyAssessmentProgress();
      }
    },

    nextQuestion() {
      self.currentQuestionIdx++; // eslint-disable-line
    },

    prevQuestion() {
      self.currentQuestionIdx--; // eslint-disable-line
    },

    restoreLastQuestion() {
      self.currentQuestionIdx = self.firstUnansweredQuestionIdx;
    },

    finish() {
      self.currentQuestionIdx = -1;
    },

    async saveAnswer(answerId) {
      const {
        classDetailsStore: { activeClass },
      } = getRoot(self);

      self.savingAnswer = true;

      // Capture before the first await so mid-flight navigation (Back,
      // opening another quiz, etc.) cannot change which question/lesson the
      // resolved save applies to.
      const savedQuestionId = self.currentQuestion.id;
      const savedLessonIdx = self.quizLessonIdx;

      try {
        const data = await saveAssessmentAnswer(
          activeClass.id,
          self.quizLesson.moduleId,
          self.quizLesson.id,
          savedQuestionId,
          answerId,
          activeClass.term ? activeClass.term.id : undefined,
          self.assessmentProgress.id
        );

        // currentSessionId is null when progressTracking.save failed silently
        // server-side. Gate local class-state mutations on it — userClass
        // derives lesson completion from progressTracking, not the assessment
        // row, so a refresh would otherwise flip the lesson back to "not
        // completed" and contradict the UI we just rendered.
        if (data.finished && data.passed && data.currentSessionId) {
          activeClass.markLessonCompleted(savedLessonIdx);
          activeClass.changeCurrentLesson(data.currentSessionId);
          if (typeof data.totalProgress === 'number') {
            activeClass.updateProgress(data.totalProgress);
          }
          if (data.newUnreadNotification) {
            activeClass.addUnreadNotification(data.newUnreadNotification);
          }
        }

        self.saveAnswerSuccess(data);
      } catch (error) {
        console.error(error);
        self.saveAnswerFailure();
      }
    },

    saveAnswerSuccess({ id, correctAnswerId, questionId, userAnswerId, finished, passed }) {
      // init() resets assessmentProgress when a quiz is opened — a save that
      // resolves after the user moved to another quiz must not write into the
      // new quiz's progress (or crash on the null in between), nor clear the
      // savingAnswer flag that now belongs to a save in the new quiz.
      if (!self.assessmentProgress || self.assessmentProgress.id !== id) return;
      self.savingAnswer = false;
      self.assessmentProgress.addProgressEntry({ questionId, userAnswerId, correctAnswerId });
      // finished/passed are null on impersonation responses (server didn't
      // compute them); leave locally-known status untouched in that case.
      if (finished != null) {
        self.assessmentProgress.setStatus({ finished, passed });
      }
    },

    saveAnswerFailure() {
      self.savingAnswer = false;
    },

    retakeQuiz() {
      self.assessmentProgress = emptyAssessmentProgress();
      self.currentQuestionIdx = 0;
    },

    setQuizActiveStatus(status) {
      self.isQuizActive = status;
    },

    setQuizToRefetch(lessonId) {
      self.currentQuizToRefetch = lessonId ? lessonId : null;
    },
  }));
